import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { cloneDeep } from "lodash";
import mixpanel from "mixpanel-browser";

import { change } from "store/root/actions";
import { useStateSelector } from "utils/field";

import Dialog, { CloseDialog } from "components/Dialog";
import Tabs from "components/Tabs";
import HelpIcon from "assets/images/icons/JSX/Help";

import {
    ALLOWED_H2K_VERSIONS,
    SWITCHOVER_TEMPERATURES,
    coreSwitchoverTemperatures,
    evaluateHybridCase,
    hybridTemperatureProgressSummary,
    refinedSwitchoverTemperatures,
} from "./eligibility";
import {
    ACCESSOR,
    averageHourlyElectricityRate,
    buildHybridAnalysis,
    loadInsightsData,
    saveHybridAnalysis,
    startHybridHeatingAnalysis,
    startHybridHeatingRefinement,
    retryHybridHeatingRefinement,
    subscribeHybridResults,
    subscribeHybridRun,
} from "./hybridAnalysis";
import Setup from "./Setup";
import Costs from "./Costs";
import Results from "./Results";

import classes from "./styles.module.scss";

const temperatureFinished = (completedTemperatures, temperature) =>
    (completedTemperatures || []).some((value) => Number(value) === Number(temperature));

const hybridRunCountPhrase = (temperatures) => {
    const { anchorTotal, refinedTotal } = hybridTemperatureProgressSummary(temperatures);
    const anchorPhrase = `${anchorTotal} anchor simulation${anchorTotal === 1 ? "" : "s"}`;
    return refinedTotal ? `${anchorPhrase} + ${refinedTotal} refined` : anchorPhrase;
};

const hybridRunProgressPhrase = (temperatures, completedTemperatures) => {
    const anchorTemps = coreSwitchoverTemperatures(temperatures);
    const refinedTemps = refinedSwitchoverTemperatures(temperatures);
    const anchorDone = anchorTemps.filter((temp) => temperatureFinished(completedTemperatures, temp)).length;
    const refinedDone = refinedTemps.filter((temp) => temperatureFinished(completedTemperatures, temp)).length;
    const anchorPart = `${anchorDone} of ${anchorTemps.length} anchor`;
    const refinedPart = refinedTemps.length ? `, ${refinedDone} of ${refinedTemps.length} refined` : "";
    return `${anchorPart}${refinedPart}`;
};

const HybridHeating = ({ modelId, modelSave }) => {
    const dispatch = useDispatch();
    const update = (...args) => dispatch(change(...args));

    const modelData = useStateSelector("model.modelData") || {};
    const hybridAnalysis = useStateSelector(ACCESSOR);
    const { packages = {} } = useStateSelector("upgrades") || {};
    const {
        packageResults = {},
        loading: resultsLoading = false,
        error: resultsError = "",
    } = useStateSelector("results") || {};

    const [ready, setReady] = useState(false);
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [panel, setPanel] = useState("setup");
    const [starting, setStarting] = useState(false);
    const [refining, setRefining] = useState(false);
    const [startError, setStartError] = useState("");
    const [refinementError, setRefinementError] = useState("");
    const [run, setRun] = useState(null);
    const [temperatureResults, setTemperatureResults] = useState({});
    const previousRunStatus = useRef(null);

    const modelFuelPrices = modelData?.location?.fuelPrices;
    const modelTimeOfUse = modelData?.general?.timeOfUse;
    const seedRef = useRef({});
    seedRef.current = { fuelPrices: modelFuelPrices, timeOfUse: modelTimeOfUse };

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setReady(false);
            try {
                const stored = await loadInsightsData(modelId);
                if (cancelled) return;
                const next = buildHybridAnalysis({
                    saved: stored?.config,
                    fuelPrices: seedRef.current.fuelPrices,
                    timeOfUse: seedRef.current.timeOfUse,
                });
                if (!stored?.config) {
                    await saveHybridAnalysis(modelId, next);
                }
                if (cancelled) return;
                dispatch(change("model.insightsData", { hybridAnalysis: next }));
                dispatch(change("model.insightsDataSaved", { hybridAnalysis: cloneDeep(next) }));
                setReady(true);
            } catch (error) {
                console.log(error);
                if (cancelled) return;
                const next = buildHybridAnalysis({
                    fuelPrices: seedRef.current.fuelPrices,
                    timeOfUse: seedRef.current.timeOfUse,
                });
                dispatch(change("model.insightsData", { hybridAnalysis: next }));
                dispatch(change("model.insightsDataSaved", { hybridAnalysis: cloneDeep(next) }));
                setReady(true);
            }
        };

        if (modelId) load();
        return () => {
            cancelled = true;
        };
    }, [modelId]);

    const { major: h2kMajor, minor: h2kMinor } = modelData.h2kVersion || {};
    const h2kVersion = h2kMajor != null && h2kMinor != null ? `v${h2kMajor}-${h2kMinor}` : "";
    const versionOk = ALLOWED_H2K_VERSIONS.includes(h2kVersion);

    const upgradePackageId = hybridAnalysis?.upgradePackageId || "base";
    const packageIsMissing = upgradePackageId !== "base" && !packages[upgradePackageId];
    const selectedPackage = upgradePackageId === "base" || packageIsMissing ? null : packages[upgradePackageId];
    const hybridCase = evaluateHybridCase(modelData.heatingCooling || {}, selectedPackage);

    useEffect(() => {
        if (!ready || !packageIsMissing) return;
        update(`${ACCESSOR}.upgradePackageId`, "base");
    }, [ready, packageIsMissing]);

    const packageOptions = [
        { value: "base", label: "Base case" },
        ...Object.keys(packages)
            .filter((pkg) => pkg !== "base")
            .map((pkg) => ({
                value: pkg,
                label: packages[pkg].name || "Upgrade package",
            })),
    ];

    const resultsPackageId = packageIsMissing ? "base" : upgradePackageId;
    const hasHot2000Results = Boolean(packageResults?.[resultsPackageId]?.metadata?.mode);
    const resultsStatus = hasHot2000Results ? "pass" : resultsLoading ? "pending" : "fail";

    const caseReady = hybridCase.ready && !packageIsMissing;
    const canRun = versionOk && hasHot2000Results && caseReady;
    const temperatures = run?.switchoverTemperatures?.length ? run.switchoverTemperatures : SWITCHOVER_TEMPERATURES;
    const hasPendingTemperatures =
        Boolean(run?.id) &&
        run?.status !== "error" &&
        temperatures.some((temperature) => !temperatureFinished(run?.completedTemperatures, temperature));
    const runActive = run?.status === "queued" || run?.status === "running" || hasPendingTemperatures;
    const finishedCount = temperatures.filter((temperature) =>
        temperatureFinished(run?.completedTemperatures, temperature)
    ).length;
    const runPackageLabel =
        !run || run.upgradePackageId === "base"
            ? "Base case"
            : packages[run.upgradePackageId]?.name || "Upgrade package";

    useEffect(() => {
        if (!modelId) return undefined;
        return subscribeHybridRun(modelId, setRun);
    }, [modelId]);

    useEffect(() => {
        if (!modelId || !run?.id) {
            setTemperatureResults({});
            return undefined;
        }
        setTemperatureResults({});
        return subscribeHybridResults(modelId, run.id, setTemperatureResults);
    }, [modelId, run?.id]);

    useEffect(() => {
        if (previousRunStatus.current === "running" && run?.status === "complete") {
            mixpanel.track("Hybrid Heating Run Complete", {
                ModelId: modelId,
                RunId: run?.id,
                UpgradePackageId: run?.upgradePackageId || "base",
                TemperatureCount: temperatures.length,
            });
        }
        if (previousRunStatus.current !== "error" && run?.status === "error") {
            mixpanel.track("Hybrid Heating Run Error", {
                ModelId: modelId,
                RunId: run?.id,
                Message: run?.error || "",
            });
        }
        previousRunStatus.current = run?.status || null;
    }, [run?.status, run?.id, run?.error, run?.upgradePackageId, modelId, temperatures.length]);

    const startRun = async ({ replaceSamePackage = false } = {}) => {
        if (!canRun || !modelId || starting || runActive) return;
        if (sweepLocked && !replaceSamePackage) return;
        setStartError("");
        setRefinementError("");
        setStarting(true);
        try {
            if (modelSave) await modelSave();
            if (hybridAnalysis) {
                await saveHybridAnalysis(modelId, hybridAnalysis);
                dispatch(change("model.insightsDataSaved", { hybridAnalysis: cloneDeep(hybridAnalysis) }));
            }
            const caseId = packageIsMissing ? "base" : upgradePackageId;
            const result = await startHybridHeatingAnalysis({
                modelId,
                expVersion: h2kVersion,
                upgradePackageId: caseId,
            });
            if (result.ok) {
                mixpanel.track("Hybrid Heating Run Start", {
                    ModelId: modelId,
                    UpgradePackageId: caseId,
                    H2kVersion: h2kVersion,
                    ReplaceSamePackage: replaceSamePackage,
                });
            } else {
                setStartError(result.message);
                mixpanel.track("Hybrid Heating Run Start Fail", {
                    ModelId: modelId,
                    UpgradePackageId: caseId,
                    Message: result.message,
                });
            }
        } catch (error) {
            console.log(error);
            setStartError("Hybrid heating analysis could not be started.");
            mixpanel.track("Hybrid Heating Run Start Fail", {
                ModelId: modelId,
                Message: "Hybrid heating analysis could not be started.",
            });
        } finally {
            setStarting(false);
        }
    };

    const startRefinement = async ({ fromC, toC, retry = false }) => {
        if (!modelId || !run?.id || runActive || refining || starting || packageChanged) return;
        if (!retry && !sweepLocked) return;
        setRefinementError("");
        setRefining(true);
        try {
            const result = retry
                ? await retryHybridHeatingRefinement({ modelId, runId: run.id, fromC, toC })
                : await startHybridHeatingRefinement({ modelId, runId: run.id, fromC, toC });
            if (result.ok) {
                mixpanel.track(retry ? "Hybrid Heating Refinement Retry" : "Hybrid Heating Refinement Start", {
                    ModelId: modelId,
                    RunId: run.id,
                    FromC: fromC,
                    ToC: toC,
                });
            } else {
                setRefinementError(result.message);
                mixpanel.track("Hybrid Heating Refinement Fail", {
                    ModelId: modelId,
                    RunId: run.id,
                    FromC: fromC,
                    ToC: toC,
                    Retry: retry,
                    Message: result.message,
                });
            }
        } catch (error) {
            console.log(error);
            setRefinementError(
                retry
                    ? "Hybrid heating refinement could not be retried."
                    : "Hybrid heating refinement could not be started."
            );
        } finally {
            setRefining(false);
        }
    };

    const timeOfUseSelected = hybridAnalysis?.electricityRateType === "timeOfUse";
    const hourlyElectricityRate = timeOfUseSelected ? averageHourlyElectricityRate(hybridAnalysis?.timeOfUse) : null;

    useEffect(() => {
        if (!ready || !timeOfUseSelected) return;
        const current = Number(hybridAnalysis?.fuelPrices?.electricity?.dollarsPerUnit);
        if (current === hourlyElectricityRate) return;
        update(`${ACCESSOR}.fuelPrices.electricity.dollarsPerUnit`, hourlyElectricityRate);
    }, [ready, timeOfUseSelected, hourlyElectricityRate, hybridAnalysis?.fuelPrices?.electricity?.dollarsPerUnit]);

    const storedPackageId = run?.upgradePackageId || "base";
    const packageChanged = Boolean(run?.id) && storedPackageId !== resultsPackageId;
    const hasStoredResults = run?.status === "complete" || (run?.completedTemperatures?.length || 0) > 0;
    const sweepLocked = run?.status === "complete" && !packageChanged;
    const canStartSweep = canRun && !runActive && !sweepLocked;
    const replaceWarning = hasStoredResults
        ? packageChanged
            ? `Running again replaces the stored results for ${runPackageLabel}.`
            : run?.status !== "complete"
              ? "Running again replaces the results stored so far."
              : ""
        : "";
    const showProgress = run && run.status !== "listen-error";
    const statusMessage = startError
        ? startError
        : run?.status === "listen-error"
          ? run.error
          : runActive
            ? `${runPackageLabel}: finished ${finishedCount} of ${temperatures.length} (${hybridRunProgressPhrase(
                  temperatures,
                  run?.completedTemperatures
              )}).`
            : run?.status === "complete"
              ? `${runPackageLabel}: all simulations finished (${hybridRunCountPhrase(temperatures)}).`
              : run?.status === "error"
                ? run.error || "The analysis stopped before every switchover temperature finished."
                : canRun
                  ? "This case is ready. Click Run analysis to begin."
                  : "Every check in the Setup tab must pass before running the analysis.";

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <div className={classes.headerWithIcon}>
                    <h4>Hybrid Heating</h4>
                    <button
                        className={classes.helpButton}
                        onClick={() => setIsInfoOpen(true)}
                        aria-label="Hybrid heating information"
                    >
                        <HelpIcon />
                    </button>
                </div>
            </div>

            <Tabs
                type="subTabs"
                stretchTabs
                tabActive={panel}
                onChange={setPanel}
                tabs={[
                    { name: "Setup", url: "setup" },
                    { name: "Costs", url: "costs" },
                    { name: "Results", url: "results" },
                ]}
            >
                <Setup
                    modelId={modelId}
                    ready={ready}
                    runActive={runActive}
                    statusMessage={statusMessage}
                    packageOptions={packageOptions}
                    upgradePackageId={resultsPackageId}
                    onPackageChange={(value) => update(`${ACCESSOR}.upgradePackageId`, value)}
                    replaceWarning={packageChanged ? replaceWarning : ""}
                    sweepLocked={sweepLocked}
                    hybridCase={hybridCase}
                    h2kVersion={h2kVersion}
                    versionOk={versionOk}
                    packageIsMissing={packageIsMissing}
                    hasHot2000Results={hasHot2000Results}
                    resultsStatus={resultsStatus}
                    resultsLoading={resultsLoading}
                    resultsError={resultsError}
                    modelData={modelData}
                    packageResults={packageResults}
                    resultsPackageId={resultsPackageId}
                />
                <Costs
                    ready={ready}
                    hybridAnalysis={hybridAnalysis}
                    update={update}
                    modelFuelPrices={modelFuelPrices}
                    modelTimeOfUse={modelTimeOfUse}
                />
                <Results
                    statusMessage={statusMessage}
                    ready={ready}
                    canRun={canRun}
                    canStartSweep={canStartSweep}
                    sweepLocked={sweepLocked}
                    replaceWarning={replaceWarning}
                    runActive={runActive}
                    starting={starting}
                    refining={refining}
                    refinementError={refinementError}
                    onRun={startRun}
                    onRefinement={startRefinement}
                    onRefinementDialogOpen={() => setRefinementError("")}
                    packageChanged={packageChanged}
                    showProgress={showProgress}
                    run={run}
                    temperatures={temperatures}
                    temperatureResults={temperatureResults}
                    hybridAnalysis={hybridAnalysis}
                    packageResults={packageResults}
                    modelData={modelData}
                />
            </Tabs>

            <Dialog open={isInfoOpen} closeActions={() => setIsInfoOpen(false)}>
                <div className={classes.infoDialog}>
                    <CloseDialog closeActions={() => setIsInfoOpen(false)} />
                    <h2>Hybrid Heating Analysis</h2>
                    <p>
                        Use EnergyPlus to investigate the performance of a hybrid heating system: an air-source heat
                        pump with a fossil-fuel backup.
                    </p>
                    <p>
                        This analysis can help you determine the optimal switchover temperature for your system by
                        comparing a variety of performance metrics at different switchover temperatures.
                    </p>
                    <p>
                        Select the case (base case or an upgrade package) you'd like to analyze in the Setup tab, then
                        run the analysis in the Results tab.
                    </p>
                    <p>
                        Fuel prices and equipment costs can be optionally entered in the Costs tab. You can update these
                        inputs after a simulation is complete to update the operating cost without running again.
                    </p>
                    <p>
                        A finished analysis stays in place until you choose to run again. Select a different case to
                        analyze another package. If you change the model, use Re-run analysis on the Results tab when
                        you decide the house has changed enough to need a new sweep. Changing the model does not start a
                        run on its own.
                    </p>
                </div>
            </Dialog>
        </div>
    );
};

export default HybridHeating;
